/* eslint-disable no-nested-ternary */
import {
  InputNumber,
  Input,
  Radio,
  Select,
  Form,
  notification,
  Switch
} from 'antd';
import i18next from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { preRequisitesLayout } from '../../utils/constants/formLayout';
import PreRequisitesSwitch from './PreRequisitesSwitch';
import FormRadioButtonWithCustom from '../../components/FormRadioButtonWithCustom/FormRadioButtonWithCustom';
import useQuotesContext from './QuotesContext';
import { ComparisonTable } from '../../components/ComparisonTable/ComparisonTable';
import { arrayConditionnalElement } from '../../utils/arrayConditionnalElement';

const { Option } = Select;

export const activitiesRCNGA = [
  'Négociant Automobiles / Mandataire',
  'Convoyeur Automobile'
];

export const MAX_FLEET_SIZE_OPTIONS = ['MAX_50', 'MAX_100'];
export const VEHICLE_VALUE_RANGE_OPTIONS = [
  'STANDARD',
  'EXTENDED_SAME_LOCATION',
  'EXTENDED_MULTI_LOCATIONS'
];
export const ENTRUSTED_VEHICLES_OPTIONS = ['MAX_3', 'MAX_15'];

const certificateNumberRegex = /^[A-Za-z]-\d{3}-[A-Za-z]{2}$/;

export const getCompanyStatuses = () => [
  {
    value: 'AUTO_ENTREPRENEUR',
    label: 'AUTO ENTREPRENEUR',
    disabled: (formValues) => formValues?.turnover > 250000
  },
  { value: 'SARL', label: 'SARL' },
  { value: 'EURL', label: 'EURL' },
  { value: 'SASU', label: 'SASU' },
  { value: 'SAS', label: 'SAS' },
  { value: 'SA', label: 'SA' },
  { value: 'EIRL', label: 'EIRL' },
  { value: 'SELARL', label: 'SELARL' }
];

export const RCNGAWK_TURNOVER_THRESHOLD_CUSTOM = 1000001;

const useRCNGAWKFields = () => {
  const { t } = useTranslation();
  const { formValues, setFormValues, setEligiblePricing } = useQuotesContext();

  const isAmendment = window.location.href.search('amendment');
  const [_, setCheckedVal] = useState('');

  const formulesComparisonDataSource = [
    {
      title: i18next.t('contracts.form.pkg_1'),
      key: 'PKG_1',
      data: {
        rc_pro_guarantee: {
          label: i18next.t('contracts.form.rc_pro_guarantee'),
          value: true
        },
        injury_damages: {
          label: i18next.t('contracts.form.injury_damages'),
          value: true
        },
        property_damages: {
          label: i18next.t('contracts.form.property_damages'),
          value: true
        },
        immaterial_damages: {
          label: i18next.t('contracts.form.immaterial_damages'),
          value: true
        },
        rc_exploitation_guarantee: {
          label: i18next.t('contracts.form.rc_exploitation_guarantee'),
          value: true
        },
        rc_environmental_damage_guarantee: {
          label: i18next.t('contracts.form.rc_environmental_damage_guarantee'),
          value: true
        },
        rc_employer: {
          label: i18next.t('contracts.form.rc_employer'),
          value: true
        },
        // Driver protection
        driver_protection: {
          label: i18next.t('contracts.form.driver_protection'),
          value: i18next.t('contracts.form.pkg_1')
        },
        individual_accident: {
          label: i18next.t('contracts.form.individual_accident'),
          value: true
        },
        criminal_defense: {
          label: i18next.t('contracts.form.criminal_defense'),
          value: i18next.t('contracts.form.pkg_1')
        },
        criminal_defense_recourse: {
          label: i18next.t('contracts.form.criminal_defense_recourse'),
          value: true
        },
        entrusted_vehicles: {
          label: i18next.t('contracts.form.entrusted_vehicles_pkg'),
          value: i18next.t('contracts.form.pkg_1')
        },
        rc_trafic_guarantee: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: false
        },
        theft_guarantee: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: false
        },
        natural_events: {
          label: i18next.t('contracts.form.natural_events'),
          value: false
        },
        natural_disasters: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: false
        },
        damage_all_accidents_guarantee: {
          label: i18next.t('contracts.form.damage_all_accidents_guarantee'),
          value: false
        },
        // vehicles intended for sale
        vehicles_intended_for_sale: {
          label: i18next.t('contracts.form.vehicles_intended_for_sale'),
          value: i18next.t('contracts.form.pkg_1')
        },
        rc_trafic_guarantee_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot_sale: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat_sale: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee_sale: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: false
        },
        theft_guarantee_sale: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: false
        },
        natural_events_sale: {
          label: i18next.t('contracts.form.natural_events'),
          value: false
        },
        natural_disasters_sale: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: false
        },
        third_party_collision_guarantee: {
          label: i18next.t('contracts.form.third_party_collision_guarantee'),
          value: false
        }
      }
    },
    {
      title: i18next.t('contracts.form.pkg_2'),
      key: 'PKG_2',
      data: {
        rc_pro_guarantee: {
          label: i18next.t('contracts.form.rc_pro_guarantee'),
          value: true
        },
        injury_damages: {
          label: i18next.t('contracts.form.injury_damages'),
          value: true
        },
        property_damages: {
          label: i18next.t('contracts.form.property_damages'),
          value: true
        },
        immaterial_damages: {
          label: i18next.t('contracts.form.immaterial_damages'),
          value: true
        },
        rc_exploitation_guarantee: {
          label: i18next.t('contracts.form.rc_exploitation_guarantee'),
          value: true
        },
        rc_environmental_damage_guarantee: {
          label: i18next.t('contracts.form.rc_environmental_damage_guarantee'),
          value: true
        },
        rc_employer: {
          label: i18next.t('contracts.form.rc_employer'),
          value: true
        },
        driver_protection: {
          label: i18next.t('contracts.form.driver_protection'),
          value: i18next.t('contracts.form.pkg_2')
        },
        individual_accident: {
          label: i18next.t('contracts.form.individual_accident'),
          value: true
        },
        criminal_defense: {
          label: i18next.t('contracts.form.criminal_defense'),
          value: i18next.t('contracts.form.pkg_2')
        },
        criminal_defense_recourse: {
          label: i18next.t('contracts.form.criminal_defense_recourse'),
          value: true
        },
        entrusted_vehicles: {
          label: i18next.t('contracts.form.entrusted_vehicles_pkg'),
          value: i18next.t('contracts.form.pkg_2')
        },
        rc_trafic_guarantee: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: false
        },
        theft_guarantee: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: false
        },
        natural_events: {
          label: i18next.t('contracts.form.natural_events'),
          value: false
        },
        natural_disasters: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: false
        },
        damage_all_accidents_guarantee: {
          label: i18next.t('contracts.form.damage_all_accidents_guarantee'),
          value: false
        },
        // vehicles intended for sale
        vehicles_intended_for_sale: {
          label: i18next.t('contracts.form.vehicles_intended_for_sale'),
          value: i18next.t('contracts.form.pkg_2')
        },
        rc_trafic_guarantee_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot_sale: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat_sale: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee_sale: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: false
        },
        theft_guarantee_sale: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: false
        },
        natural_events_sale: {
          label: i18next.t('contracts.form.natural_events'),
          value: false
        },
        natural_disasters_sale: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: false
        },
        third_party_collision_guarantee: {
          label: i18next.t('contracts.form.third_party_collision_guarantee'),
          value: false
        }
      }
    },
    {
      title: i18next.t('contracts.form.pkg_3'),
      key: 'PKG_3',
      data: {
        rc_pro_guarantee: {
          label: i18next.t('contracts.form.rc_pro_guarantee'),
          value: true
        },
        injury_damages: {
          label: i18next.t('contracts.form.injury_damages'),
          value: true
        },
        property_damages: {
          label: i18next.t('contracts.form.property_damages'),
          value: true
        },
        immaterial_damages: {
          label: i18next.t('contracts.form.immaterial_damages'),
          value: true
        },
        rc_exploitation_guarantee: {
          label: i18next.t('contracts.form.rc_exploitation_guarantee'),
          value: true
        },
        rc_environmental_damage_guarantee: {
          label: i18next.t('contracts.form.rc_environmental_damage_guarantee'),
          value: true
        },
        rc_employer: {
          label: i18next.t('contracts.form.rc_employer'),
          value: true
        },
        driver_protection: {
          label: i18next.t('contracts.form.driver_protection'),
          value: i18next.t('contracts.form.pkg_3')
        },
        individual_accident: {
          label: i18next.t('contracts.form.individual_accident'),
          value: true
        },
        criminal_defense: {
          label: i18next.t('contracts.form.criminal_defense'),
          value: i18next.t('contracts.form.pkg_3')
        },
        criminal_defense_recourse: {
          label: i18next.t('contracts.form.criminal_defense_recourse'),
          value: true
        },
        entrusted_vehicles: {
          label: i18next.t('contracts.form.entrusted_vehicles_pkg'),
          value: i18next.t('contracts.form.pkg_3')
        },
        rc_trafic_guarantee: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: true
        },
        theft_guarantee: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: true
        },
        natural_events: {
          label: i18next.t('contracts.form.natural_events'),
          value: true
        },
        natural_disasters: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: true
        },
        damage_all_accidents_guarantee: {
          label: i18next.t('contracts.form.damage_all_accidents_guarantee'),
          value: false
        },
        vehicles_intended_for_sale: {
          label: i18next.t('contracts.form.vehicles_intended_for_sale'),
          value: i18next.t('contracts.form.pkg_3')
        },
        // vehicles intended for sale
        rc_trafic_guarantee_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot_sale: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat_sale: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee_sale: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: true
        },
        theft_guarantee_sale: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: true
        },
        natural_events_sale: {
          label: i18next.t('contracts.form.natural_events'),
          value: true
        },
        natural_disasters_sale: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: true
        },
        third_party_collision_guarantee: {
          label: i18next.t('contracts.form.third_party_collision_guarantee'),
          value: false
        }
      }
    },
    {
      title: i18next.t('contracts.form.pkg_4'),
      key: 'PKG_4',
      data: {
        rc_pro_guarantee: {
          label: i18next.t('contracts.form.rc_pro_guarantee'),
          value: true
        },
        injury_damages: {
          label: i18next.t('contracts.form.injury_damages'),
          value: true
        },
        property_damages: {
          label: i18next.t('contracts.form.property_damages'),
          value: true
        },
        immaterial_damages: {
          label: i18next.t('contracts.form.immaterial_damages'),
          value: true
        },
        rc_exploitation_guarantee: {
          label: i18next.t('contracts.form.rc_exploitation_guarantee'),
          value: true
        },
        rc_environmental_damage_guarantee: {
          label: i18next.t('contracts.form.rc_environmental_damage_guarantee'),
          value: true
        },
        rc_employer: {
          label: i18next.t('contracts.form.rc_employer'),
          value: true
        },
        driver_protection: {
          label: i18next.t('contracts.form.driver_protection'),
          value: i18next.t('contracts.form.pkg_4')
        },
        individual_accident: {
          label: i18next.t('contracts.form.individual_accident'),
          value: true
        },
        criminal_defense: {
          label: i18next.t('contracts.form.criminal_defense'),
          value: i18next.t('contracts.form.pkg_4')
        },
        criminal_defense_recourse: {
          label: i18next.t('contracts.form.criminal_defense_recourse'),
          value: true
        },
        entrusted_vehicles: {
          label: i18next.t('contracts.form.entrusted_vehicles_pkg'),
          value: i18next.t('contracts.form.pkg_4')
        },
        rc_trafic_guarantee: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: true
        },
        theft_guarantee: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: true
        },
        natural_events: {
          label: i18next.t('contracts.form.natural_events'),
          value: true
        },
        natural_disasters: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: true
        },
        damage_all_accidents_guarantee: {
          label: i18next.t('contracts.form.damage_all_accidents_guarantee'),
          value: true
        },
        // vehicles intended for sale
        vehicles_intended_for_sale: {
          label: i18next.t('contracts.form.vehicles_intended_for_sale'),
          value: i18next.t('contracts.form.pkg_4')
        },
        rc_trafic_guarantee_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot_sale: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat_sale: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee_sale: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: true
        },
        theft_guarantee_sale: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: true
        },
        natural_events_sale: {
          label: i18next.t('contracts.form.natural_events'),
          value: true
        },
        natural_disasters_sale: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: true
        },
        third_party_collision_guarantee: {
          label: i18next.t('contracts.form.third_party_collision_guarantee'),
          value: true
        }
      }
    },
    {
      title: i18next.t('contracts.form.PKG_5'),
      key: 'PKG_5',
      data: {
        rc_pro_guarantee: {
          label: i18next.t('contracts.form.rc_pro_guarantee'),
          value: true
        },
        injury_damages: {
          label: i18next.t('contracts.form.injury_damages'),
          value: true
        },
        property_damages: {
          label: i18next.t('contracts.form.property_damages'),
          value: true
        },
        immaterial_damages: {
          label: i18next.t('contracts.form.immaterial_damages'),
          value: true
        },
        rc_exploitation_guarantee: {
          label: i18next.t('contracts.form.rc_exploitation_guarantee'),
          value: true
        },
        rc_environmental_damage_guarantee: {
          label: i18next.t('contracts.form.rc_environmental_damage_guarantee'),
          value: true
        },
        rc_employer: {
          label: i18next.t('contracts.form.rc_employer'),
          value: true
        },
        driver_protection: {
          label: i18next.t('contracts.form.driver_protection'),
          value: i18next.t('contracts.form.PKG_5')
        },
        individual_accident: {
          label: i18next.t('contracts.form.individual_accident'),
          value: true
        },
        criminal_defense: {
          label: i18next.t('contracts.form.criminal_defense'),
          value: i18next.t('contracts.form.PKG_5')
        },
        criminal_defense_recourse: {
          label: i18next.t('contracts.form.criminal_defense_recourse'),
          value: true
        },
        entrusted_vehicles: {
          label: i18next.t('contracts.form.entrusted_vehicles_pkg'),
          value: i18next.t('contracts.form.PKG_5')
        },
        rc_trafic_guarantee: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: false
        },
        theft_guarantee: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: false
        },
        natural_events: {
          label: i18next.t('contracts.form.natural_events'),
          value: false
        },
        natural_disasters: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: false
        },
        damage_all_accidents_guarantee: {
          label: i18next.t('contracts.form.damage_all_accidents_guarantee'),
          value: false
        },
        // vehicles intended for sale
        vehicles_intended_for_sale: {
          label: i18next.t('contracts.form.vehicles_intended_for_sale'),
          value: i18next.t('contracts.form.PKG_5')
        },
        rc_trafic_guarantee_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: true
        },
        rc_trafic_guarantee_corporel_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: true
        },
        riot_sale: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: true
        },
        attentat_sale: {
          label: i18next.t('contracts.form.attack'),
          value: true
        },
        fire_guarantee_sale: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: false
        },
        theft_guarantee_sale: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: false
        },
        natural_events_sale: {
          label: i18next.t('contracts.form.natural_events'),
          value: false
        },
        natural_disasters_sale: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: false
        },
        third_party_collision_guarantee: {
          label: i18next.t('contracts.form.third_party_collision_guarantee'),
          value: false
        }
      }
    },
    {
      title: i18next.t('contracts.form.ceilings'),
      key: 'PAYMENT_LIMIT',
      selectable: false,
      data: {
        rc_pro_guarantee: {
          label: i18next.t('contracts.form.rc_pro_guarantee'),
          value: i18next.t('contracts.form.rc_pro_guarantee_ceiling')
        },
        injury_damages: {
          label: i18next.t('contracts.form.injury_damages'),
          value: i18next.t('contracts.form.injury_damages_ceiling')
        },
        property_damages: {
          label: i18next.t('contracts.form.property_damages'),
          value: i18next.t('contracts.form.property_damages_ceiling')
        },
        immaterial_damages: {
          label: i18next.t('contracts.form.immaterial_damages'),
          value: i18next.t('contracts.form.immaterial_damages_ceiling')
        },
        rc_exploitation_guarantee: {
          label: i18next.t('contracts.form.rc_exploitation_guarantee'),
          value: i18next.t('contracts.form.rc_exploitation_guarantee_ceiling')
        },
        rc_environmental_damage_guarantee: {
          label: i18next.t('contracts.form.rc_environmental_damage_guarantee'),
          value: i18next.t(
            'contracts.form.rc_environmental_damage_guarantee_ceiling'
          )
        },
        rc_employer: {
          label: i18next.t('contracts.form.rc_employer'),
          value: i18next.t('contracts.form.rc_employer_ceiling')
        },
        driver_protection: {
          label: i18next.t('contracts.form.driver_protection'),
          value: i18next.t('contracts.form.ceilings')
        },
        individual_accident: {
          label: i18next.t('contracts.form.individual_accident'),
          value: i18next.t('contracts.form.individual_accident_ceiling')
        },
        criminal_defense: {
          label: i18next.t('contracts.form.criminal_defense'),
          value: i18next.t('contracts.form.ceilings')
        },
        criminal_defense_recourse: {
          label: i18next.t('contracts.form.criminal_defense_recourse'),
          value: i18next.t('contracts.form.criminal_defense_recourse_ceiling')
        },
        entrusted_vehicles: {
          label: i18next.t('contracts.form.entrusted_vehicles_pkg'),
          value: i18next.t('contracts.form.ceilings')
        },
        rc_trafic_guarantee: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: i18next.t(
            'contracts.form.rc_trafic_guarantee_property_ceiling'
          )
        },
        rc_trafic_guarantee_corporel: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: i18next.t(
            'contracts.form.rc_trafic_guarantee_immaterial_ceiling'
          )
        },
        riot: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: i18next.t('contracts.form.riot_guarantee_ceiling')
        },
        attentat: {
          label: i18next.t('contracts.form.attack'),
          value: i18next.t('contracts.form.attack_guarantee_ceiling')
        },
        fire_guarantee: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: i18next.t('contracts.form.fire_guarantee_ceiling')
        },
        theft_guarantee: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: i18next.t('contracts.form.theft_guarantee_ceiling')
        },
        natural_events: {
          label: i18next.t('contracts.form.natural_events'),
          value: i18next.t('contracts.form.natural_events_ceiling')
        },
        natural_disasters: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: i18next.t('contracts.form.natural_disasters_ceiling')
        },
        damage_all_accidents_guarantee: {
          label: i18next.t('contracts.form.damage_all_accidents_guarantee'),
          value: i18next.t(
            'contracts.form.damage_all_accidents_guarantee_ceiling'
          )
        },
        // vehicles intended for sale
        vehicles_intended_for_sale: {
          label: i18next.t('contracts.form.vehicles_intended_for_sale'),
          value: i18next.t('contracts.form.ceilings')
        },
        rc_trafic_guarantee_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: i18next.t(
            'contracts.form.rc_trafic_guarantee_property_ceiling'
          )
        },
        rc_trafic_guarantee_corporel_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: i18next.t(
            'contracts.form.rc_trafic_guarantee_immaterial_ceiling'
          )
        },
        riot_sale: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: i18next.t('contracts.form.riot_guarantee_ceiling')
        },
        attentat_sale: {
          label: i18next.t('contracts.form.attack_guarantee'),
          value: i18next.t('contracts.form.attack_guarantee_ceiling')
        },
        fire_guarantee_sale: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: i18next.t('contracts.form.fire_guarantee_ceiling')
        },
        theft_guarantee_sale: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: i18next.t('contracts.form.theft_guarantee_ceiling')
        },
        natural_events_sale: {
          label: i18next.t('contracts.form.natural_events'),
          value: i18next.t('contracts.form.natural_events_ceiling')
        },
        natural_disasters_sale: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: i18next.t('contracts.form.natural_disasters_ceiling')
        },
        third_party_collision_guarantee: {
          label: i18next.t('contracts.form.third_party_collision_guarantee'),
          value: i18next.t(
            'contracts.form.third_party_collision_guarantee_ceiling'
          )
        }
      }
    },
    {
      title: i18next.t('contracts.form.deductibles'),
      key: 'DEDUCTIBLE_AMOUNT',
      selectable: false,
      data: {
        rc_pro_guarantee: {
          label: i18next.t('contracts.form.rc_pro_guarantee'),
          value: i18next.t('contracts.form.rc_pro_guarantee_deductible')
        },
        injury_damages: {
          label: i18next.t('contracts.form.injury_damages'),
          value: i18next.t('contracts.form.injury_damages_deductible')
        },
        property_damages: {
          label: i18next.t('contracts.form.property_damages'),
          value: i18next.t('contracts.form.property_damages_deductible')
        },
        immaterial_damages: {
          label: i18next.t('contracts.form.immaterial_damages'),
          value: i18next.t('contracts.form.immaterial_damages_deductible')
        },
        rc_exploitation_guarantee: {
          label: i18next.t('contracts.form.rc_exploitation_guarantee'),
          value: i18next.t(
            'contracts.form.rc_exploitation_guarantee_deductible'
          )
        },
        rc_environmental_damage_guarantee: {
          label: i18next.t('contracts.form.rc_environmental_damage_guarantee'),
          value: i18next.t(
            'contracts.form.rc_environmental_damage_guarantee_deductible'
          )
        },
        rc_employer: {
          label: i18next.t('contracts.form.rc_employer'),
          value: i18next.t('contracts.form.rc_employer_deductible')
        },
        driver_protection: {
          label: i18next.t('contracts.form.driver_protection'),
          value: i18next.t('contracts.form.deductibles')
        },
        individual_accident: {
          label: i18next.t('contracts.form.individual_accident'),
          value: i18next.t('contracts.form.individual_accident_deductible')
        },
        criminal_defense: {
          label: i18next.t('contracts.form.criminal_defense'),
          value: i18next.t('contracts.form.deductibles')
        },
        criminal_defense_recourse: {
          label: i18next.t('contracts.form.criminal_defense_recourse'),
          value: i18next.t(
            'contracts.form.criminal_defense_recourse_deductible'
          )
        },
        entrusted_vehicles: {
          label: i18next.t('contracts.form.entrusted_vehicles_pkg'),
          value: i18next.t('contracts.form.deductibles')
        },
        rc_trafic_guarantee: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: i18next.t(
            'contracts.form.rc_trafic_guarantee_property_deductible'
          )
        },
        rc_trafic_guarantee_corporel: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: i18next.t(
            'contracts.form.rc_trafic_guarantee_immaterial_deductible'
          )
        },
        riot: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: i18next.t('contracts.form.riot_guarantee_deductible')
        },
        attentat: {
          label: i18next.t('contracts.form.attack'),
          value: i18next.t('contracts.form.attack_guarantee_deductible')
        },
        fire_guarantee: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: i18next.t('contracts.form.fire_guarantee_deductible')
        },
        theft_guarantee: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: i18next.t('contracts.form.theft_guarantee_deductible')
        },
        natural_events: {
          label: i18next.t('contracts.form.natural_events'),
          value: i18next.t('contracts.form.natural_events_deductible')
        },
        natural_disasters: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: i18next.t('contracts.form.natural_disasters_deductible')
        },
        damage_all_accidents_guarantee: {
          label: i18next.t('contracts.form.damage_all_accidents_guarantee'),
          value: i18next.t(
            'contracts.form.damage_all_accidents_guarantee_deductible'
          )
        },
        vehicles_intended_for_sale: {
          label: i18next.t('contracts.form.vehicles_intended_for_sale'),
          value: i18next.t('contracts.form.deductibles')
        },
        // vehicles intended for sale
        rc_trafic_guarantee_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_property'),
          value: i18next.t(
            'contracts.form.rc_trafic_guarantee_property_deductible'
          )
        },
        rc_trafic_guarantee_corporel_sale: {
          label: i18next.t('contracts.form.rc_trafic_guarantee_immaterial'),
          value: i18next.t(
            'contracts.form.rc_trafic_guarantee_immaterial_deductible'
          )
        },
        riot_sale: {
          label: i18next.t('contracts.form.riot_guarantee'),
          value: i18next.t('contracts.form.riot_guarantee_deductible')
        },
        attentat_sale: {
          label: i18next.t('contracts.form.attack_guarantee'),
          value: i18next.t('contracts.form.attack_guarantee_deductible')
        },
        fire_guarantee_sale: {
          label: i18next.t('contracts.form.fire_guarantee'),
          value: i18next.t('contracts.form.fire_guarantee_deductible_selling')
        },
        theft_guarantee_sale: {
          label: i18next.t('contracts.form.theft_guarantee'),
          value: i18next.t('contracts.form.theft_guarantee_deductible_selling')
        },
        natural_events_sale: {
          label: i18next.t('contracts.form.natural_events'),
          value: i18next.t('contracts.form.natural_events_deductible')
        },
        natural_disasters_sale: {
          label: i18next.t('contracts.form.natural_disasters'),
          value: i18next.t('contracts.form.natural_disasters_deductible')
        },
        third_party_collision_guarantee: {
          label: i18next.t('contracts.form.third_party_collision_guarantee'),
          value: i18next.t(
            'contracts.form.third_party_collision_guarantee_deductible'
          )
        }
      }
    }
  ];

  useEffect(() => {
    if (
      formValues?.turnover === 1000001 ||
      formValues?.withdrawal_drivin_license ||
      formValues?.liquidation_property_or_legal_recovery ||
      formValues?.guaranteed_liability_involved ||
      formValues?.compliant_installations ||
      formValues?.political_person_exposed ||
      formValues?.more_than_two_claims ||
      formValues?.bodily_claim ||
      formValues?.driving_while_intoxicated
    ) {
      setEligiblePricing(false);
      notification.error({
        message: t('help.not_eligible_pricing_title'),
        description: t('help.not_eligible_pricing'),
        duration: 7,
        key: 'oneTime'
      });
    } else {
      setEligiblePricing(true);
    }
  }, [
    formValues?.withdrawal_drivin_license,
    formValues?.liquidation_property_or_legal_recovery,
    formValues?.guaranteed_liability_involved,
    formValues?.compliant_installations,
    formValues?.political_person_exposed,
    formValues?.more_than_two_claims,
    formValues?.bodily_claim,
    formValues?.driving_while_intoxicated,
    formValues?.turnover
  ]);

  const rcngawkFields = [
    {
      name: ['activity'],
      tooltip: false,
      rules: [{ required: true }],
      input: (
        <Radio.Group buttonStyle="solid">
          {activitiesRCNGA.map((activity) => (
            <Radio.Button key={activity} value={activity}>
              {activity}
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['turnover'],
      tooltip: {
        name: 'turnover_rcnga'
      },
      rules: [
        { required: true },
        {
          validator: (_, value) =>
            formValues && value > 0
              ? Promise.resolve()
              : Promise.reject(new Error('Ce champs est requis.'))
        }
      ],
      input: <InputNumber min={0} max={5000000} style={{ width: 120 }} />,
      step: 0
    },
    {
      name: ['company_status'],
      rules: [{ required: true }],
      input: (
        <FormRadioButtonWithCustom
          fieldName="company_status"
          options={getCompanyStatuses()}
        />
      ),
      step: 0
    },
    {
      name: ['max_fleet_size'],
      label: 'max_fleet_size.label',
      tooltip: false,
      rules: [
        { required: true, message: t('contracts.form.max_fleet_size.required') }
      ],
      input: (
        <Radio.Group buttonStyle="solid">
          {MAX_FLEET_SIZE_OPTIONS.map((value) => (
            <Radio.Button key={value} value={value}>
              {t(`contracts.form.max_fleet_size.${value}`)}
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['vehicle_value_range'],
      label: 'vehicle_value_range.label',
      tooltip: false,
      rules: [
        {
          required: true,
          message: t('contracts.form.vehicle_value_range.required')
        }
      ],
      input: (
        <Radio.Group
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {VEHICLE_VALUE_RANGE_OPTIONS.map((value) => (
            <Radio key={value} value={value}>
              {t(`contracts.form.vehicle_value_range.${value}`)}
            </Radio>
          ))}
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['entrusted_vehicles'],
      label: 'entrusted_vehicles.label',
      tooltip: false,
      rules: [
        {
          required: true,
          message: t('contracts.form.entrusted_vehicles.required')
        }
      ],
      input: (
        <Radio.Group buttonStyle="solid">
          {ENTRUSTED_VEHICLES_OPTIONS.map((value) => (
            <Radio.Button key={value} value={value}>
              {t(`contracts.form.entrusted_vehicles.${value}`)}
            </Radio.Button>
          ))}
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['guarantee_package'],
      rules: [{ required: true }],
      tooltip: true,
      layout: {
        labelCol: { span: 0 },
        wrapperCol: { xs: 24, sm: 24, md: 24, lg: 24, xl: 24 }
      },
      tooltipLabel: t('contracts.form.tooltip.rcngawk.guarantee_package'),
      input: (
        <ComparisonTable
          dataSource={formulesComparisonDataSource}
          value={formValues?.guarantee_package}
        />
      ),
      step: 0
    },
    ...arrayConditionnalElement(
      {
        name: ['glass_breakage_guarantee'],
        label: 'glass_breakage_guarantee.label',
        tooltip: true,
        tooltipLabel: t('contracts.form.tooltip.glass_breakage_guarantee'),
        rules: [
          {
            required: true,
            message: t('contracts.form.glass_breakage_guarantee.required')
          }
        ],
        input: (
          <Radio.Group buttonStyle="solid">
            <Radio.Button value>{t('quotes.form.switch.true')}</Radio.Button>
            <Radio.Button value={false}>
              {t('quotes.form.switch.false')}
            </Radio.Button>
          </Radio.Group>
        ),
        step: 0
      },
      (formValues?.guarantee_package === 'PKG_3' ||
        formValues?.guarantee_package === 'PKG_4') &&
        formValues?.turnover > 1000000
    ),
    ...arrayConditionnalElement(
      {
        name: ['hail_guarantee'],
        label: 'hail_guarantee.label',
        tooltip: true,
        tooltipLabel: t('contracts.form.tooltip.hail_guarantee'),
        rules: [
          {
            required: true,
            message: t('contracts.form.hail_guarantee.required')
          }
        ],
        input: (
          <Radio.Group buttonStyle="solid">
            <Radio.Button value>{t('quotes.form.switch.true')}</Radio.Button>
            <Radio.Button value={false}>
              {t('quotes.form.switch.false')}
            </Radio.Button>
          </Radio.Group>
        ),
        step: 0
      },
      (formValues?.guarantee_package === 'PKG_3' ||
        formValues?.guarantee_package === 'PKG_4') &&
        formValues?.turnover > 1000000
    ),
    {
      name: ['fraction_type'],
      label: 'fraction_type',
      rules: [{ required: true }],
      tooltip: true,
      tooltipLabel: t('contracts.form.tooltip.fraction_type'),
      input: (
        <Radio.Group buttonStyle="solid" disabled={isAmendment !== -1}>
          <Radio.Button value="MONTHLY">
            {t('contracts.form.options.MONTHLY')}
          </Radio.Button>
          <Radio.Button value="QUARTERLY">
            {t('contracts.form.options.QUARTERLY')}
          </Radio.Button>
          <Radio.Button value="BIANNUAL">
            {t('contracts.form.options.BIANNUAL')}
          </Radio.Button>
          <Radio.Button value="YEARLY">
            {t('contracts.form.options.YEARLY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['w_garage_certificate_number'],
      tooltip: true,
      tooltipLabel: t(
        'contracts.form.tooltip.rcngawk.w_garage_certificate_number'
      ),
      rules: [{ required: true }],
      input: <InputNumber min={0} max={10} />,
      step: 0
    },
    ...Array.from(
      { length: formValues?.w_garage_certificate_number || 0 },
      (_, index) => ({
        name: ['w_garage_certificate', index],
        label: 'w_garage_certificate',
        step: 0,
        rules: [
          { required: true },
          {
            pattern: certificateNumberRegex,
            message: t('contracts.form.w_garage_certificate_number_format')
          }
        ],
        input: <Input />
      })
    ),
    {
      name: ['legal_protection'],
      input: <Switch disabled />,
      valuePropName: 'checked',
      tooltip: true,
      step: 0
    },
    {
      name: ['extended_legal_protection'],
      label: 'extended_legal_protection_base',
      input: <PreRequisitesSwitch />,
      valuePropName: 'checked',
      tooltip: true,
      step: 0
    },
    {
      name: ['withdrawal_drivin_license'],
      step: 1
    },
    {
      name: ['liquidation_property_or_legal_recovery'],
      step: 1
    },
    {
      name: ['guaranteed_liability_involved'],
      step: 1
    },
    {
      name: ['more_than_two_claims'],
      step: 1
    },
    {
      name: ['compliant_installations'],
      step: 1
    },
    {
      name: ['bodily_claim'],
      step: 1
    },
    {
      name: ['driving_while_intoxicated'],
      step: 1
    },
    {
      name: ['political_person_exposed'],
      step: 1
    }
  ];

  return {
    rcngawkFields: rcngawkFields.map((f) => {
      if (f.step === 1)
        return {
          ...f,
          input: (
            <PreRequisitesSwitch
              onChange={(val) => {
                setFormValues({
                  ...formValues,
                  [f.name[0]]: val
                });
                setCheckedVal(f.name[0]);
              }}
            />
          ),
          labelAlign: 'left',
          valuePropName: 'checked',
          withDivider: true,
          layout: preRequisitesLayout
        };
      return f;
    })
  };
};

export default useRCNGAWKFields;
